import { getSubscriptionLists } from 'services/endpoints/subscriptionList'
import { useAuthStore } from 'store'
import {
  type ListRow,
  type OrderType,
  type SortType,
  useSubscriptionListsCacheStore
} from 'store/subscriptionListsCacheStore'

const STALE_TIME_MS = 24 * 60 * 60 * 1000

const inFlight = new Map<string, Promise<void>>()

export const refreshSubscriptionListsCache = async (
  sort: SortType = 'recently_added',
  order: OrderType = 'asc'
): Promise<void> => {

    const userId = useAuthStore.getState().userProfile?.id
    if (!userId) return

  const store = useSubscriptionListsCacheStore.getState()
  const key = store.makeKey(sort, order)

  const existing = inFlight.get(key)
  if (existing) return await existing

  const entry = store.getEntry(sort, order)

  if (
    entry?.fetchedAt &&
    Date.now() - entry.fetchedAt < STALE_TIME_MS
  ) {
    return await Promise.resolve()
  }

  const task = (async () => {
    const lists: ListRow[] = []
    let page = 1

    // Track the cache state before requesting.
    // Local changes during a request must not be overwritten.
    const startUpdatedAt = entry?.updatedAt ?? 0

    while (true) {
      const response = await getSubscriptionLists(sort, order, page)

      if (!response) {
        throw new Error('Failed to fetch subscription lists')
      }

      const pageLists = response.page?.data?.data ?? []

      lists.push(...pageLists)

      if (!response.nextCursor) break

      page = response.nextCursor
    }

    const currentStore = useSubscriptionListsCacheStore.getState()
    const currentEntry = currentStore.getEntry(sort, order)

    if ((currentEntry?.updatedAt ?? 0) !== startUpdatedAt) {
      // A local mutation happened during the request.
      // Preserve the newer cache rather than replacing it.
      return
    }
    if (useAuthStore.getState().userProfile?.id !== userId) {
    return
    }
    currentStore.setLists(
      sort,
      order,
      lists.map(data => ({ data }))
    )
  })()

  inFlight.set(key, task)

  return await task.finally(() => {
    inFlight.delete(key)
  })
}